import { memo, useCallback } from "react";

// Helpers
import { useTranslate } from "helpers";

// UI
import { DatePicker } from "ui/components/DatePicker";
import { Dropdown } from "ui/components/Dropdown/Dropdown";
import { DropdownOption } from "ui/components/Dropdown/Dropdown/types";
import { TextField } from "ui/components/TextField/TextField";

import {
  initialVehicleTransferSearchForm,
  TRANSFER_ACTIVE_OPTIONS,
  VEHICLE_LOCATION_TYPE_OPTIONS,
} from "../../consts";
import { VehicleTransferSearchFiltersProps } from "./types";

/**
 * Filtri popisa premještaja. Endpoint vraća cijelu povijest vozila bez parametara,
 * pa se filtriranje radi na klijentu — popis je kratak (jedan redak po premještaju).
 */
const TransferSearchFilters: React.FC<VehicleTransferSearchFiltersProps> = (props) => {
  const { formValues, onSearchChange } = props;

  const { t } = useTranslate();

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      onSearchChange({ ...formValues, search: e.target.value });
    },
    [formValues, onSearchChange]
  );

  const handleLocationTypeChange = useCallback(
    (_e: any, _name: string, item: DropdownOption) => {
      onSearchChange({ ...formValues, locationType: item.value || null });
    },
    [formValues, onSearchChange]
  );

  // Dropdown radi sa stringovima, a filter je trostanjski (sve / trenutne / prošle).
  const handleActiveChange = useCallback(
    (_e: any, _name: string, item: DropdownOption) => {
      onSearchChange({
        ...formValues,
        isActive: item.value ? item.value === "true" : null,
      });
    },
    [formValues, onSearchChange]
  );

  const handleDateChange = useCallback(
    (name: string, value: Date | null) => {
      onSearchChange({ ...formValues, [name]: value });
    },
    [formValues, onSearchChange]
  );

  const handleResetFormClick = useCallback(() => {
    onSearchChange(initialVehicleTransferSearchForm);
  }, [onSearchChange]);

  return (
    <div className="flex flex-wrap items-end gap-3">
      <div className="min-w-[260px]">
        <TextField
          type="text"
          name="search"
          value={formValues.search}
          onChange={handleSearchChange}
          placeholder="Lokacija, vozač ili napomena"
          startIcon="/media/icons/search.svg"
          cssClassName="bg-white"
        />
      </div>
      <div className="min-w-[200px]">
        <Dropdown
          name="locationType"
          placeholder="Vrsta lokacije"
          value={formValues.locationType}
          options={VEHICLE_LOCATION_TYPE_OPTIONS}
          onChange={handleLocationTypeChange}
          cssClassName="bg-white"
          width="full"
        />
      </div>
      <div className="min-w-[200px]">
        <Dropdown
          name="isActive"
          placeholder="Sve lokacije"
          value={
            formValues.isActive === null ? null : String(formValues.isActive)
          }
          options={TRANSFER_ACTIVE_OPTIONS}
          onChange={handleActiveChange}
          cssClassName="bg-white"
          width="full"
        />
      </div>
      <div className="w-[150px]">
        <DatePicker
          name="validFromFrom"
          label="Datum premještaja od"
          value={formValues.validFromFrom}
          onChange={handleDateChange}
          cssClassName="bg-white"
        />
      </div>
      <div className="w-[150px]">
        <DatePicker
          name="validFromTo"
          label="do"
          value={formValues.validFromTo}
          onChange={handleDateChange}
          cssClassName="bg-white"
        />
      </div>
      <div className="flex items-center pb-2">
        <span
          onClick={handleResetFormClick}
          className="text-xs font-bold cursor-pointer text-gray-700"
        >
          {t("admin.reset")}
        </span>
      </div>
    </div>
  );
};

export default memo(TransferSearchFilters);
